/**
 * Статусы проверки расценок (Ф2): цвет бейджа и русская подпись — одна карта
 * для списка и карточки.
 *
 * Список раскрашивал по своей функции, которая знала только `pending` и
 * `parsed`, и всё остальное, включая успешный анализ, получало красный, а на
 * экран выводился английский код статуса (замечание F07 экспертизы
 * 03.11.2026). Карта типизирована как `Record<RateCheck['status'], …>`: новый
 * статус в типе без подписи здесь не соберётся.
 *
 * Статусы — `rate_checks.status` (миграция 0058 + Stage D): разбор файла
 * (`pending` → `parsed` | `failed`) и анализ (`analyzing` → `analyzed` |
 * `analyze_failed`).
 */
import type { RateCheck } from '@shared/api';

export type RateCheckStatus = RateCheck['status'];

export interface RateCheckStatusView {
  label: string;
  tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
  /** Идёт работа — экран опрашивает статус, пока она не закончится. */
  inProgress: boolean;
}

export const RATE_CHECK_STATUSES: Record<RateCheckStatus, RateCheckStatusView> = {
  pending: { label: 'Файл разбирается', tone: 'warning', inProgress: true },
  parsed: { label: 'Разобран, анализ не запускался', tone: 'neutral', inProgress: false },
  failed: { label: 'Ошибка разбора файла', tone: 'danger', inProgress: false },
  analyzing: { label: 'Идёт анализ', tone: 'info', inProgress: true },
  analyzed: { label: 'Анализ готов', tone: 'success', inProgress: false },
  analyze_failed: { label: 'Ошибка анализа', tone: 'danger', inProgress: false },
};

/** Подпись и цвет статуса. Незнакомый статус (сервер новее экрана) — без кода. */
export function rateCheckStatusView(status: string): RateCheckStatusView {
  return (
    RATE_CHECK_STATUSES[status as RateCheckStatus] ?? {
      label: 'Статус не распознан',
      tone: 'neutral',
      inProgress: false,
    }
  );
}

/** Можно ли запускать анализ из этого состояния. */
export function canAnalyze(status: string): boolean {
  return status === 'parsed' || status === 'analyzed' || status === 'analyze_failed';
}

/**
 * Вопрос перед повторным анализом, если находки уже размечены: новый анализ
 * заменяет находки, и решения с комментариями остаются только в журнале
 * действий (замечание A07 экспертизы 03.11.2026).
 */
export function rerunConfirmText(annotated: number): string {
  return (
    `Повторный анализ заменит находки. Размеченных находок: ${annotated} — их решения и ` +
    'комментарии будут сброшены (копия сохранится в журнале действий). Запустить анализ?'
  );
}

/**
 * Что сказать, когда таблица расхождений пуста. `null` — пока идёт работа:
 * заключение «расхождений нет» до конца анализа ложно (F01), вместо него
 * страница показывает «идёт анализ».
 */
export function discrepanciesEmptyText(status: string): string | null {
  if (rateCheckStatusView(status).inProgress) return null;
  switch (status) {
    case 'analyzed':
      return 'Расхождений по выбранным фильтрам нет.';
    case 'analyze_failed':
      return 'Анализ завершился ошибкой, находок нет. Запустите анализ ещё раз на странице проверки.';
    case 'failed':
      return 'Файл не удалось разобрать — анализ недоступен. Загрузите файл заново.';
    default:
      return 'Анализ ещё не запускался — вернитесь на страницу проверки и нажмите «Запустить анализ».';
  }
}

/** Интервал опроса статуса: только пока идёт работа, иначе опроса нет. */
export const RATE_CHECK_POLL_MS = 3000;

export function rateCheckPollInterval(status: string | undefined): number {
  return status && rateCheckStatusView(status).inProgress ? RATE_CHECK_POLL_MS : 0;
}
